import React from "react";
import cn from "classnames";
import { UseFormRegister, FieldValues } from "react-hook-form";

type InputProps = {
  label: string;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  type?: "text" | "number" | "date" | "datetime-local" | "color";
  value?: string;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
};

type Ref = HTMLInputElement;
type Props = InputProps & Partial<Omit<ReturnType<UseFormRegister<FieldValues>>, "onChange">>;

const Input = React.forwardRef<Ref, Props>(
  (
    { label, placeholder, className, disabled, type = "text", ...props },
    ref
  ) => {
    // Was `const id = nanoid()` at module scope, which is evaluated once per module
    // load, not per instance - so every Input in the app rendered the same DOM id and
    // clicking any label focused whichever field mounted first. useId gives each
    // instance its own stable, SSR-safe id.
    const id = React.useId();

    return (
      <div className={cn("flex flex-col gap-y-1.5 grow", className && className)}>
        <label htmlFor={id} className="text-slate-600 text-sm font-medium">
          {label}
        </label>
        <input
          id={id}
          type={type}
          placeholder={placeholder}
          disabled={disabled}
          {...props}
          ref={ref}
          className={cn(
            "rounded-md border border-slate-300 bg-white text-sm px-3 py-2 min-w-0 placeholder:text-slate-400 focus:outline-none focus:border-vg-blue focus:ring-[3px] focus:ring-vg-blue/15 transition-colors",
            disabled && "bg-slate-50 text-slate-500 cursor-not-allowed"
          )}
        />
      </div>
    );
  }
);

Input.displayName = "Input";

export default Input;
